import { Box, FormHelperText, Grid, useTheme } from '@mui/material';
import type { FormikContextType } from 'formik';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';

import type { EmailFormikType } from '@core/formik/email';
import {
  applySignature,
  hasSignature,
  signatureBlock,
  stripSignature,
} from '@core/utils/emailSignature';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import 'react-quill/dist/quill.snow.css';

import { useSlabQuery } from '../../hooks/useSlabQuery';
import { Input } from '../Input/Input';
import { MultiEmailApiLookupInput } from '../MultiEmailApiLookupInput/MultiEmailApiLookupInput';
import { CustomToolbar } from './CustomToolBar';
import { NylasContext } from './Login/NylasContext';

/**
 * @description EmailComposerProps is the type used for the properties
 * of the exported component EmailComposer
 */
type EmailComposerProps = {
  onChange?: (value: string) => void;
  formikBag: FormikContextType<EmailFormikType>;
};

const SIGNATURES_PER_PAGE = 5;

/**
 * @description An EmailComposer is a self-contained component that includes
 * fields for the To, CC, and BCC properties of the SendMessageInput type
 * using a MultiEmailApiLookupInput field, a field for a subject line, and a rich text
 * editor for the email's body
 */
export const EmailComposer = ({ onChange, formikBag }: EmailComposerProps): ReactElement => {
  const theme = useTheme();
  const quillRef = useRef<ReactQuill>(null);
  const [signaturesPage, setSignaturesPage] = useState(0);
  const { data: signatures } = useSlabQuery('GET users email signatures', {
    queryParams: { page: signaturesPage, perPage: SIGNATURES_PER_PAGE },
  });

  const signaturesTotalPages = hasValue(signatures)
    ? Math.ceil(signatures.count / SIGNATURES_PER_PAGE)
    : 0;

  /**
   * Replace the body's signature block with `signature`, keeping whatever the
   * user wrote above it. Quill is uncontrolled (`defaultValue`), so the new body
   * has to be pushed into the editor as well as into Formik.
   */
  const replaceSignature = (signature: string): void => {
    if (!hasValue(quillRef.current)) {
      return;
    }
    const quill = quillRef.current.getEditor();
    const { body } = formikBag.values;

    quill.setText('');
    quill.clipboard.dangerouslyPasteHTML(0, stripSignature(body));

    // Leave the caret above the signature so the user carries on typing in
    // their own text rather than inside the signature.
    const caret = Math.max(quill.getLength() - 1, 0);
    quill.clipboard.dangerouslyPasteHTML(caret, signatureBlock(signature));
    quill.setSelection(caret, 0);

    SetFormikValue(formikBag, 'body', applySignature(body, signature), true);
  };

  useEffect(() => {
    const defaultSignature = signatures?.items.find((it) => it.isDefault);
    if (!hasStringValue(defaultSignature?.content) || hasSignature(formikBag.values.body)) {
      return;
    }
    replaceSignature(defaultSignature.content);
  }, [signatures?.items]);

  // TODO: #2543 this should return the trimmed+formatted value, then set the
  // `formikBag` state, and then call the optional `onChange` handler at the
  // call-sites.
  const onEditorChange = (newValue: string, editor: ReactQuill.UnprivilegedEditor): void => {
    const isEmpty = editor.getText() === '\n';
    const trimmedValue = isEmpty ? '' : newValue;
    // HACK(sc-1308): Quill generates paragraph breaks for newlines, but doesn't
    // display any extra spacing between those paragraphs, so we update the
    // styles of those paragraphs to avoid unexpected vertical spacing in the
    // final email.
    const formattedValue = trimmedValue.replaceAll('<p>', '<p style="margin:0">');

    SetFormikValue(formikBag, 'body', formattedValue, true);
    formikBag.setFieldTouched('body', true, false);
    onChange?.(formattedValue);
  };

  const { touched, error: bodyError } = formikBag.getFieldMeta('body');
  const hasBodyError = touched && bodyError !== undefined;

  return (
    <NylasContext>
      <Box display='flex' gap='1rem' flexDirection='column'>
        <Grid container spacing={1} columns={2}>
          <Grid item xs={2}>
            <MultiEmailApiLookupInput name='to' label='To*' />
          </Grid>
          <Grid item xs={1}>
            <MultiEmailApiLookupInput name='cc' label='CC' />
          </Grid>
          <Grid item xs={1}>
            <MultiEmailApiLookupInput name='bcc' label='BCC' />
          </Grid>
          <Grid item xs={2}>
            <Input name='subject' label='Subject*' />
          </Grid>
          <Grid item xs={2}>
            <Box display='flex' flexDirection='column'>
              <CustomToolbar
                onSignatureSelect={(signature): void => replaceSignature(signature.content)}
                signatures={signatures?.items ?? []}
                signaturesPage={signaturesPage}
                setSignaturesPage={setSignaturesPage}
                signaturesTotalPages={signaturesTotalPages}
              />
              <Box
                sx={{
                  height: '15rem',
                  paddingBottom: '3rem',
                  '& .quill': {
                    height: '100%',
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.fontSize,
                  },
                  '& .ql-container': {
                    borderColor: hasBodyError
                      ? theme.palette.error.main
                      : theme.palette.SlabGray['400'],
                    borderWidth: '0 1px 1px 1px',
                    borderStyle: 'solid',
                  },
                  '& #toolbar': {
                    borderColor: hasBodyError
                      ? theme.palette.error.main
                      : theme.palette.SlabGray['400'],
                    borderWidth: '1px 1px 0 1px',
                    borderStyle: 'solid',
                    borderRadius: '4px 4px 0 0',
                  },
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  defaultValue={formikBag.values.body}
                  onChange={(v, d, source, editor): void => {
                    onEditorChange(v, editor);
                  }}
                  modules={{
                    toolbar: {
                      container: '#toolbar',
                    },
                  }}
                  onBlur={(range, source, editor): void => {
                    onEditorChange(editor.getHTML(), editor);
                  }}
                  theme='snow'
                />
              </Box>
              <Box>
                <FormHelperText error>{hasBodyError ? bodyError : ' '}</FormHelperText>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </NylasContext>
  );
};
